import React from "react";
import { Badge, Empty, Tag } from "antd";
import Chart from "react-apexcharts";
import moment from "moment";
import {
  FaChalkboardTeacher,
  FaUserCheck,
  FaUserClock,
} from "react-icons/fa";
import { AiOutlineClockCircle } from "react-icons/ai";
import { Card, CardTitle, KpiStatTile, LinkRow, SectionShell } from "../blocks";
import {
  LEAVE_TYPE_META,
  SECTION_COLORS,
  SERIES,
  STATUS,
  axisLabelStyle,
  baseChart,
  baseGrid,
  donutBase,
  legendBase,
} from "../chartTheme";

const ACCENT = SECTION_COLORS.staff;

const StaffSection = ({ overview, staffOps, onNavigate }) => {
  // Fill all 7 trailing days so gaps render as zero
  const staffAttendanceDays = Array.from({ length: 7 }, (_, i) => {
    const d = moment()
      .startOf("day")
      .subtract(6 - i, "days");
    const row = (staffOps.attendanceTrend || []).find(
      (t) => t.date === d.format("YYYY-MM-DD")
    );
    return {
      label: d.format("ddd"),
      onTime: row ? row.onTime : 0,
      late: row ? row.late : 0,
    };
  });
  const hasStaffAttendanceData = staffAttendanceDays.some(
    (d) => d.onTime > 0 || d.late > 0
  );

  const departmentDistribution = staffOps.departmentDistribution || [];
  const hasDepartmentData = departmentDistribution.length > 0;

  const teacherStudentRatio =
    overview.totalTeachers > 0
      ? Math.round(overview.totalStudents / overview.totalTeachers)
      : 0;

  const staffAttendanceBar = {
    options: {
      chart: baseChart("iskool360-staff-attendance", "bar"),
      // Status semantics: on-time = good, late = warning (paired with legend labels)
      colors: [STATUS.good, STATUS.warning],
      plotOptions: { bar: { columnWidth: "45%", borderRadius: 4 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: staffAttendanceDays.map((d) => d.label),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: axisLabelStyle,
      },
      yaxis: { labels: { formatter: (v) => Math.round(v) } },
      tooltip: { y: { formatter: (v) => `${v} instructors` } },
      legend: { ...legendBase, position: "top", horizontalAlign: "right" },
      grid: baseGrid,
    },
    series: [
      { name: "On Time", data: staffAttendanceDays.map((d) => d.onTime) },
      { name: "Late", data: staffAttendanceDays.map((d) => d.late) },
    ],
  };

  // Leave-type slots in fixed categorical order — Permission/Leave/Sick never
  // swap colors as the underlying counts change.
  const leaveTypeKeys = Object.keys(LEAVE_TYPE_META);
  const leaveTypeSeries = leaveTypeKeys.map(
    (key) => staffOps.leaveRequests?.pendingByType?.[key] || 0
  );
  const hasLeaveTypeData = leaveTypeSeries.some((v) => v > 0);

  const leaveTypeDonut = {
    options: {
      chart: baseChart("iskool360-leave-type", "donut"),
      labels: leaveTypeKeys.map((key) => LEAVE_TYPE_META[key].label),
      colors: [SERIES[0], SERIES[1], SERIES[2]],
      ...donutBase("Pending"),
    },
    series: leaveTypeSeries,
  };

  const departmentBar = {
    options: {
      chart: baseChart("iskool360-department", "bar"),
      colors: [ACCENT],
      plotOptions: { bar: { horizontal: true, barHeight: "45%", borderRadius: 4 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: departmentDistribution.map((d) => d.departmentName),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: axisLabelStyle,
      },
      tooltip: { y: { formatter: (v) => `${v} staff` } },
      grid: baseGrid,
    },
    series: [
      { name: "Staff", data: departmentDistribution.map((d) => d.count) },
    ],
  };

  return (
    <SectionShell
      id="staff"
      title="Staff"
      subtitle={`${staffOps.totalInstructors || overview.totalTeachers} instructors`}
      accentColor={ACCENT}
      extra={
        teacherStudentRatio > 0 && (
          <Tag color="blue">1 teacher : {teacherStudentRatio} students</Tag>
        )
      }
    >
      <div className="k360-grid k360-cols-2-4 k360-mb">
        <KpiStatTile
          title="Total Instructors"
          value={staffOps.totalInstructors || overview.totalTeachers}
          caption="Active teaching staff"
          icon={<FaChalkboardTeacher size={17} />}
          chipClass="bg-violet-50 text-violet-600"
          accentColor={ACCENT}
        />
        <KpiStatTile
          title="On Time Today"
          value={staffOps.attendanceToday?.onTime || 0}
          caption="Checked in on schedule"
          icon={<FaUserCheck size={17} />}
          chipClass="bg-emerald-50 text-emerald-600"
          captionClass="text-emerald-600 font-semibold"
          accentColor={ACCENT}
        />
        <KpiStatTile
          title="Late Today"
          value={staffOps.attendanceToday?.late || 0}
          caption="Checked in late"
          icon={<AiOutlineClockCircle size={17} />}
          chipClass="bg-amber-50 text-amber-600"
          accentColor={ACCENT}
        />
        <KpiStatTile
          title="Not Checked-in"
          value={staffOps.attendanceToday?.notCheckedIn || 0}
          caption={`${staffOps.attendanceToday?.onLeaveOrPermission || 0} on leave/permission`}
          icon={<FaUserClock size={16} />}
          chipClass="bg-rose-50 text-rose-600"
          captionClass="text-rose-500 font-semibold"
          accentColor={ACCENT}
        />
      </div>

      <div className="k360-grid k360-cols-2 k360-mb">
        <Card accentColor={ACCENT}>
          <CardTitle
            title="Leave & Permission Requests"
            subtitle="Pending, approved, and rejected this month"
          />
          <div className="flex gap-2 flex-wrap mb-4">
            <Tag color="red">{staffOps.leaveRequests?.pending || 0} pending</Tag>
            <Tag color="green">
              {staffOps.leaveRequests?.approvedThisMonth || 0} approved this month
            </Tag>
            <Tag color="default">
              {staffOps.leaveRequests?.rejectedThisMonth || 0} rejected this month
            </Tag>
          </div>
          <LinkRow
            icon={<FaUserClock size={16} />}
            iconBg="#fff1f2"
            iconColor="#f43f5e"
            label="Staff leave requests"
            caption="Permission / leave awaiting review"
            right={
              <Badge
                count={staffOps.leaveRequests?.pending || 0}
                showZero
                style={{
                  backgroundColor:
                    (staffOps.leaveRequests?.pending || 0) > 0
                      ? "#f43f5e"
                      : "#cbd5e1",
                }}
              />
            }
            onClick={() =>
              onNavigate("/dashboard/admin/menu/instructor-attendance")
            }
          />
        </Card>

        <Card accentColor={ACCENT}>
          <CardTitle
            title="Punctuality Trend"
            subtitle="On-time vs late check-ins — last 7 days"
          />
          {hasStaffAttendanceData ? (
            <Chart
              options={staffAttendanceBar.options}
              series={staffAttendanceBar.series}
              type="bar"
              height={280}
            />
          ) : (
            <Empty
              description="No staff check-ins in the last 7 days"
              className="py-12"
            />
          )}
        </Card>
      </div>

      <div className="k360-grid k360-cols-2">
        <Card accentColor={ACCENT}>
          <CardTitle
            title="Department Distribution"
            subtitle="Teaching staff per department"
          />
          {hasDepartmentData ? (
            <Chart
              options={departmentBar.options}
              series={departmentBar.series}
              type="bar"
              height={280}
            />
          ) : (
            <Empty description="No departments yet" className="py-12" />
          )}
        </Card>

        <Card accentColor={ACCENT}>
          <CardTitle
            title="Leave Type Breakdown"
            subtitle="Pending permission / leave / sick requests"
          />
          {hasLeaveTypeData ? (
            <Chart
              options={leaveTypeDonut.options}
              series={leaveTypeDonut.series}
              type="donut"
              height={280}
            />
          ) : (
            <Empty description="No pending leave requests" className="py-12" />
          )}
        </Card>
      </div>
    </SectionShell>
  );
};

export default StaffSection;
